import { useState } from "react";
import { MapPin, CheckCircle2, Bell } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function CoverageChecker() {
  const [pin, setPin] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [contact, setContact] = useState({ name: "", phone: "", email: "" });

  const check = async (e) => {
    e?.preventDefault();
    if (pin.length < 6) {
      toast.error("Enter a 6-digit pincode.");
      return;
    }
    setBusy(true);
    setWaitlistSubmitted(false);
    try {
      const { data } = await api.get(`/coverage/check?pincode=${pin}`);
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not check.");
    } finally {
      setBusy(false);
    }
  };

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!contact.phone && !contact.email) {
      toast.error("Phone or email please.");
      return;
    }
    try {
      await api.post("/waitlist", {
        pincode: pin,
        phone: contact.phone,
        email: contact.email,
        name: contact.name,
      });
      setWaitlistSubmitted(true);
      // Re-check to refresh the count
      check();
      toast.success("You're on the list. We'll text the moment a rikshaw rolls in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sign up.");
    }
  };

  return (
    <section
      className="bg-jow-cream border-t-2 border-jow-gold/40 py-16 md:py-20"
      data-testid="coverage-checker"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="label-overline text-jow-gold">Check your PIN</span>
          <h2 className="font-display text-4xl md:text-5xl text-jow-navy mt-2">
            Does a rikshaw roll past your street?
          </h2>
          <p className="text-sm text-jow-navy/60 mt-3 max-w-xl mx-auto">
            Enter your pincode. If we're live, we'll tell you the rikshaw and city. If not, join
            the waitlist — the more folks wait, the faster we deploy.
          </p>
        </div>

        {/* Input */}
        <form
          onSubmit={check}
          className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-6"
        >
          <div className="flex-1 relative">
            <MapPin
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-jow-navy/40"
            />
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="e.g. 110016"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="w-full bg-white border border-jow-navy/20 pl-10 pr-3 py-3 text-base focus:border-jow-gold outline-none"
              data-testid="coverage-pin-input"
            />
          </div>
          <button
            type="submit"
            disabled={busy || pin.length !== 6}
            className="btn-primary-jow px-6"
            data-testid="coverage-check-btn"
          >
            {busy ? "Checking…" : "Check coverage"}
          </button>
        </form>

        {/* Result */}
        {result && result.covered && (
          <div
            className="max-w-xl mx-auto bg-white border-2 border-green-500/40 p-6 text-center"
            data-testid="coverage-covered"
          >
            <CheckCircle2 size={32} className="text-green-600 mx-auto" />
            <p className="label-overline text-green-700 mt-3">Good news</p>
            <h3 className="font-display text-2xl text-jow-navy mt-2">
              We're live in {result.rikshaw?.city || "your city"}
            </h3>
            <p className="text-sm text-jow-navy/65 mt-2">
              {result.rikshaw?.code} rolls past pincode {result.pincode}. Browse the schedule to
              catch us.
            </p>
            <a href="/find-us" className="btn-primary-jow mt-5 inline-flex">
              See the rikshaw map
            </a>
          </div>
        )}

        {result && !result.covered && !waitlistSubmitted && (
          <div
            className="max-w-xl mx-auto bg-white border border-jow-navy/10 p-6"
            data-testid="coverage-uncovered"
          >
            <div className="text-center mb-4">
              <Bell size={28} className="text-jow-pink mx-auto" />
              <p className="label-overline text-jow-pink mt-2">Not there yet</p>
              <h3 className="font-display text-2xl text-jow-navy mt-2">{pin}</h3>
              <p className="text-sm text-jow-navy/65 mt-2">
                <strong className="text-jow-navy">
                  {result.neighborhood_count} folks
                </strong>{" "}
                near you are waiting. Only{" "}
                <strong className="text-jow-pink">{result.remaining_needed}</strong> more signups
                unlock a rikshaw here.
              </p>
              <div className="mt-4 h-2 bg-jow-navy/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-jow-gold transition-all"
                  style={{
                    width: `${Math.min(
                      100,
                      (result.neighborhood_count / result.unlock_threshold) * 100,
                    )}%`,
                  }}
                  data-testid="coverage-progress"
                />
              </div>
              <p className="text-[10px] text-jow-navy/50 mt-1 uppercase tracking-widest">
                Unlock at {result.unlock_threshold} signups
              </p>
            </div>

            <form onSubmit={joinWaitlist} className="space-y-2 mt-6">
              <input
                type="text"
                placeholder="Your name"
                value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                className="w-full bg-jow-cream/50 border border-jow-navy/15 px-3 py-2 text-sm"
                data-testid="wl-name"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="tel"
                  placeholder="Phone (+91…)"
                  value={contact.phone}
                  onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                  className="bg-jow-cream/50 border border-jow-navy/15 px-3 py-2 text-sm"
                  data-testid="wl-phone"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  className="bg-jow-cream/50 border border-jow-navy/15 px-3 py-2 text-sm"
                  data-testid="wl-email"
                />
              </div>
              <button
                type="submit"
                className="btn-primary-jow w-full"
                data-testid="wl-submit"
              >
                Join waitlist for {pin}
              </button>
            </form>
          </div>
        )}

        {waitlistSubmitted && result && (
          <div
            className="max-w-xl mx-auto bg-jow-navy text-jow-cream p-6 text-center"
            data-testid="coverage-waitlisted"
          >
            <h3 className="font-display text-2xl">You're in.</h3>
            <p className="text-sm text-jow-cream/70 mt-2">
              We'll text you the day a rikshaw starts pulling up at {pin}.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
